import { clinicalAccessError } from './accessError';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Link } from '@tanstack/react-router';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { useOrgTimezone } from '@/lib/timezone';
import { formatInstant } from '@/lib/utils/date';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { MidwiferyFlowsheet, NewbornFollowup } from './MidwiferyFlowsheet';
import { eventHeads } from './projection';
import { flowsheetUrl } from './api';
import type { ClinicalEvent } from './types';
export interface DyadData {
  episode_id: string;
  mother_patient_id: string;
  babies: {
    fetus_subject_id: string;
    newborn_subject_id: string;
    newborn_patient_id: string | null;
    birth_head: ClinicalEvent;
    active: boolean;
    living_care: boolean;
    demographics_need_review?: boolean;
  }[];
  delivery: {
    status: string;
    needs_outcome_review: boolean;
    gtpal: Record<string, number> | null;
    prior_pregnancy_snapshot: Record<string, number | null>;
  };
  maternal_references: ClinicalEvent[];
  tasks: { id: string; label?: string; title?: string; subject_id?: string; [key: string]: unknown }[];
}
interface NewbornChartData {
  dyad: DyadData;
  events: ClinicalEvent[];
  next_cursor: number;
}
export async function fetchNewbornChart(orgId: string, patientId: string): Promise<NewbornChartData | null> {
  let cursor = 0;
  const events: ClinicalEvent[] = [];
  for (;;) {
    let data: NewbornChartData;
    try {
      data = (
        await clientApi.get<NewbornChartData>(`${flowsheetUrl(orgId)}/newborns/${patientId}/chart`, {
          params: { after: cursor, limit: 500 },
        })
      ).data;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404 && cursor === 0) return null;
      throw e;
    }
    events.push(...data.events);
    if (data.events.length < 500) return { ...data, events };
    if (data.next_cursor <= cursor) throw new Error('Newborn chart cursor did not advance');
    cursor = data.next_cursor;
  }
}
export function NewbornOrMidwiferyChart({ patientId }: { patientId: string }) {
  const { organization } = useAuth();
  const { permissions } = usePermissions();
  const orgId = organization?.organization_id;
  const chart = useQuery({
    queryKey: ['newborn-chart', orgId, patientId],
    enabled: !!orgId && permissions.includes('read_flowsheet'),
    queryFn: () => fetchNewbornChart(orgId!, patientId),
    retry: false,
  });
  if (!permissions.includes('read_flowsheet')) return <MidwiferyFlowsheet patientId={patientId} />;
  if (chart.isLoading) return <Skeleton className="h-24 w-full" />;
  if (chart.isError)
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {clinicalAccessError(chart.error, 'Clinical chart could not be loaded.')}
          <Button variant="outline" onClick={() => chart.refetch()}>
            Retry
          </Button>
          <a className="ml-3 underline" href="/offline-flowsheets.html">
            Open prepared offline charts
          </a>
        </AlertDescription>
      </Alert>
    );
  return chart.data ? (
    <NewbornRecord key={`${orgId}:${patientId}`} chart={chart.data} patientId={patientId} />
  ) : (
    <MidwiferyFlowsheet patientId={patientId} />
  );
}
function display(value: unknown): string {
  if (value === null || value === undefined) return 'Not recorded';
  if (Array.isArray(value)) return value.map(display).join('; ');
  if (typeof value === 'object')
    return Object.entries(value as Record<string, unknown>)
      .map(([key, v]) => `${key.replaceAll('_', ' ')}: ${display(v)}`)
      .join(' · ');
  return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
}
function NewbornRecord({ chart, patientId }: { chart: NewbornChartData; patientId: string }) {
  const { timeZone } = useOrgTimezone();
  const [history, setHistory] = useState(false);
  const [followup, setFollowup] = useState(false);
  const baby = chart.dyad.babies.find((b) => b.newborn_patient_id === patientId);
  const rows = (history ? chart.events : eventHeads(chart.events)).slice().sort((a, b) => b.sequence - a.sequence);
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Newborn birth record</CardTitle>
          <CardDescription>
            Observations are linked from the birth encounter and retain their original author, time, and amendment
            history.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {baby?.demographics_need_review && (
            <Alert>
              <AlertDescription>
                The birth details changed. Review this newborn’s date of birth and sex in Details; existing demographics
                have been retained.
              </AlertDescription>
            </Alert>
          )}
          {!baby?.living_care && (
            <Alert>
              <AlertDescription>
                This record is retained for history. Current birth evidence does not identify an active live birth.
              </AlertDescription>
            </Alert>
          )}
          {baby && (
            <p>
              Birth: {formatInstant(baby.birth_head.occurred_at, 'MMM d, yyyy HH:mm:ss', timeZone)} ·{' '}
              {String(baby.birth_head.payload.outcome)}
            </p>
          )}
          <Link to="/patients/$patientId" params={{ patientId: chart.dyad.mother_patient_id }}>
            Open linked maternal birth chart
          </Link>
          <Button variant="outline" className="min-h-11" aria-pressed={history} onClick={() => setHistory(!history)}>
            {history ? 'Show current revisions' : 'Show complete amendment history'}
          </Button>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Observation</TableHead>
                <TableHead>Finding</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{formatInstant(e.occurred_at, 'MMM d HH:mm:ss', timeZone)}</TableCell>
                  <TableCell>{e.event_type.replaceAll('_', ' ')}</TableCell>
                  <TableCell>{display(e.payload)}</TableCell>
                  <TableCell>
                    {e.author_initials} · {e.author_role}
                  </TableCell>
                  <TableCell>
                    {e.status}
                    {e.reason ? ` · ${e.reason}` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!rows.length && <p>No newborn observations are recorded yet.</p>}
        </CardContent>
      </Card>
      {baby?.living_care && (
        <>
          <Button className="min-h-11" variant="outline" onClick={() => setFollowup(!followup)}>
            {followup ? 'Close newborn follow-up entry' : 'Open scoped newborn follow-up entry'}
          </Button>
          {followup && (
            <NewbornFollowup
              episodeId={chart.dyad.episode_id}
              motherPatientId={chart.dyad.mother_patient_id}
              subjectId={baby.newborn_subject_id}
            />
          )}
        </>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Maternal source references</CardTitle>
          <CardDescription>
            These source observations support newborn review. A missing result is not a negative result.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Finding</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chart.dyad.maternal_references.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{formatInstant(e.occurred_at, 'MMM d HH:mm:ss', timeZone)}</TableCell>
                  <TableCell>{e.event_type}</TableCell>
                  <TableCell>{display(e.payload)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
